import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useMemo } from 'react'

import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { CacheKeys } from '../lib/cache'
import { isBlank, sanitizeDriverLicenseNumber } from '../lib/sanitization'
import { type PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { formatDateForApi } from '../lib/utils'

export enum DriverStatus {
  ACTIVE = 'ACTIVE',
  REMOVED = 'REMOVED',
  PENDING_ADD = 'PENDING_ADD',
  PENDING_CHANGE = 'PENDING_CHANGE',
  PENDING_REMOVE = 'PENDING_REMOVE',
  PENDING_APPROVED = 'PENDING_APPROVED',
  PENDING_REJECTED = 'PENDING_REJECTED',
}

export interface InvitedDriver {
  id: string
  firstName: string
  lastName: string
  email?: string | null
  mobile?: string | null
  licenseNumber: string
  licenseState: string
  status: DriverStatus
  dob: string
  dateOfHire: string
  cdlDate?: string | null
  yearsOfExperience?: number | null
  violationsCount?: number | null
  accidentsCount?: number | null
  suspensionsCount?: number | null
}

export interface PendingDriverChange {
  id: string
  status: DriverStatus
  firstName: string
  lastName: string
  licenseNumber: string
  licenseState: string
  dob: string
  dateOfHire: string
  yearsOfExperience: number | null
  violationsCount: number | null
  accidentsCount: number | null
  suspensionsCount: number | null
}

export interface Driver extends InvitedDriver {
  dateCreated: string
  dateUpdated: string
  dateOfHire: string
  dateOnPolicy: string
  telematicsId: string
  cdl: string
  isBusinessOwner: boolean
  pendingChange?: PendingDriverChange | null
}

/**
 * Everything else on a driver is optional — a name and a date of birth is all the roster needs.
 * The license is the one exception: carriers substitute their own state for a blank one, so half
 * a license is submitted against a state the driver may never have held. Both or neither.
 */
export function isValidDriver(driver: InvitedDriver) {
  return Boolean(
    driver.firstName && driver.lastName && driver.dob && !driver.licenseNumber === !driver.licenseState
  )
}

/**
 * What carriers need that a driver lacks, in the words its card shows them; mirrors the server's
 * DRIVER_REQUIRED_FIELDS. Stricter than isValidDriver: the license is required too, and a placeholder
 * such as X00000000 is no license. A missing CDL/hire date and a license that misfits its state are
 * shown on the confirm step as warnings rather than gates, so neither counts here.
 */
export function missingDriverFields(driver: InvitedDriver): string[] {
  return [
    isBlank(driver.firstName) && 'first name',
    isBlank(driver.lastName) && 'last name',
    isBlank(driver.dob) && 'date of birth',
    isBlank(driver.licenseNumber)
      ? 'license number'
      : !sanitizeDriverLicenseNumber(driver.licenseNumber) && 'a real license number',
    isBlank(driver.licenseState) && 'license state',
  ].filter((field): field is string => !!field)
}

/** Ready for the confirmation-step gate: nothing carriers need is missing. */
export function isDriverCarrierReady(driver: InvitedDriver): boolean {
  return missingDriverFields(driver).length === 0
}

const LICENSE_CHECK_DEBOUNCE_MS = 400

/** The confirm step's advisory note on a licence's format, or null. In the body: a licence number is PII. */
export function checkDriverLicense(licenseNumber: string, licenseState: string): Promise<string | null> {
  return axios
    .post('/api/v1/insure/driver-license/check/', {
      license_number: licenseNumber,
      license_state: licenseState,
    })
    .then((response) => (response.data as { warning: string | null }).warning)
}

/** The note for a licence being typed, asked once the typing pauses. */
export function useLicenseWarning(licenseNumber: string, licenseState: string): string | null {
  const number = useDebouncedValue(licenseNumber.trim(), LICENSE_CHECK_DEBOUNCE_MS)
  const state = useDebouncedValue(licenseState.trim(), LICENSE_CHECK_DEBOUNCE_MS)
  const { data } = useQuery({
    queryKey: [CacheKeys.DRIVER_LICENSE_CHECK, state, number],
    queryFn: () => checkDriverLicense(number, state),
    enabled: !!number,
    staleTime: Infinity,
  })
  return number ? (data ?? null) : null
}

/** Neither date on file, so carriers price the driver as brand new. Mirrors carrier_required_fields.py. */
export function hasUnknownExperience(driver: Pick<InvitedDriver, 'cdlDate' | 'dateOfHire'>): boolean {
  return !driver.cdlDate && !driver.dateOfHire
}

function driversUrl(orgId: string) {
  return `/api/v1/insure/organisations/${orgId}/drivers/`
}

function getDrivers(orgId: string, page: number): Promise<PaginatedResults<Driver>> {
  return axios
    .get(driversUrl(orgId), { params: { page } })
    .then((response) => toCamelCase(response.data) as PaginatedResults<Driver>)
}

function parsePageParam(url: string | null): number | null {
  if (!url) return null
  const params = new URL(url, window.location.origin).searchParams
  const page = params.get('page')
  return page ? Number(page) : null
}

interface UseDriversProps {
  orgId: string | undefined
  enabled?: boolean
  shouldFetchAllPages?: boolean
}

const DATE_FIELDS = ['dob', 'dateOfHire', 'dateOnPolicy', 'cdlDate'] as const

export function formatDriverDates<T extends Partial<Driver>>(data: T): T {
  const formatted = { ...data }
  for (const field of DATE_FIELDS) {
    if (field in formatted) {
      formatted[field] = formatDateForApi(formatted[field]) as string
    }
  }
  return formatted
}

export function addDriver(orgId: string, data: Partial<Driver>): Promise<Driver> {
  return axios
    .post(driversUrl(orgId), toSnakeCase(formatDriverDates(data)))
    .then((response) => toCamelCase(response.data) as Driver)
}

export function editDriver(orgId: string, id: string, data: Partial<Driver>): Promise<Driver> {
  return axios
    .patch(`${driversUrl(orgId)}${id}/`, toSnakeCase(formatDriverDates(data)))
    .then((response) => toCamelCase(response.data) as Driver)
}

export function deleteDriver(orgId: string, id: string): Promise<void> {
  return axios.delete(`${driversUrl(orgId)}${id}/`)
}

export function fetchRemovedDrivers(orgId: string): Promise<Driver[]> {
  return axios.get(`${driversUrl(orgId)}removed/`).then((response) => toCamelCase(response.data) as Driver[])
}

export function restoreDriver(orgId: string, id: string): Promise<Driver> {
  return axios
    .post(`${driversUrl(orgId)}${id}/restore/`)
    .then((response) => toCamelCase(response.data) as Driver)
}

export function approveDriver(orgId: string, id: string): Promise<Driver> {
  return axios
    .post(`${driversUrl(orgId)}${id}/approve/`)
    .then((response) => toCamelCase(response.data) as Driver)
}

export function rejectDriver(orgId: string, id: string): Promise<Driver> {
  return axios
    .post(`${driversUrl(orgId)}${id}/reject/`)
    .then((response) => toCamelCase(response.data) as Driver)
}

/** `ownerOperator` fills a driver's missing email and leaves the owner's answers where the driver is blank. */
export function designateOwnerDriver(
  orgId: string,
  id: string,
  ownerOperator?: { email: string }
): Promise<Driver> {
  return axios
    .post(
      `${driversUrl(orgId)}${id}/designate-owner/`,
      ownerOperator ? { email: ownerOperator.email, keep_owner_answers: true } : undefined
    )
    .then((response) => toCamelCase(response.data) as Driver)
}

export function undesignateOwnerDriver(orgId: string, id: string): Promise<Driver> {
  return axios
    .post(`${driversUrl(orgId)}${id}/undesignate-owner/`)
    .then((response) => toCamelCase(response.data) as Driver)
}

/** One driver by id, fetched only when asked for (a details dialog). */
export function useDriver(orgId: string | undefined, id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [CacheKeys.DRIVERS, orgId, id],
    queryFn: () => axios.get(`${driversUrl(orgId!)}${id}/`).then((r) => toCamelCase(r.data) as Driver),
    enabled: enabled && !!orgId && !!id,
  })
}

export function useDrivers({ orgId, enabled = true, shouldFetchAllPages = false }: UseDriversProps) {
  const queryEnabled = enabled && !!orgId
  const { data, fetchNextPage, hasNextPage, isFetching, refetch, isLoading } = useInfiniteQuery({
    queryKey: [CacheKeys.DRIVERS, orgId],
    queryFn: ({ pageParam = 1 }) => getDrivers(orgId!, pageParam),
    enabled: queryEnabled,
    refetchOnWindowFocus: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => parsePageParam(lastPage.next),
  })

  const drivers = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data])

  useEffect(() => {
    if (shouldFetchAllPages && !isFetching && hasNextPage && queryEnabled) {
      fetchNextPage()
    }
  }, [shouldFetchAllPages, isFetching, hasNextPage, fetchNextPage, queryEnabled])

  return {
    drivers,
    refetch,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  }
}
